import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Compass, ChevronRight } from 'lucide-react';
import {
  WORLD_MAP_IMAGE,
  WORLD_MAP_IMAGE_MOBILE,
  WORLD_MAP_IMAGE_PNG,
  WORLD_MAP_DIMENSIONS,
  REALMS,
} from '../config/worldData';
import { computeGatewayLayout, realmBounds } from '../lib/gatewayLayout';
import './WorldMapPage.css';

const ART_RATIO = WORLD_MAP_DIMENSIONS.width / WORLD_MAP_DIMENSIONS.height;
const BOUNDS = realmBounds(REALMS);

// Light colours sampled from the artwork (the data's theme colours are tuned for panels, not glow).
const REALM_GLOW = {
  asgard: '255, 216, 107',
  vanaheim: '155, 229, 138',
  alfheim: '231, 198, 255',
  'jotunheim-north': '191, 221, 255',
  jotunheim: '191, 221, 255',
  midgard: '255, 194, 107',
  svartalfheim: '255, 138, 61',
  niflheim: '189, 235, 255',
  muspelheim: '255, 106, 61',
  helheim: '127, 240, 200',
};
const DEFAULT_GLOW = '251, 191, 36';

// Yggdrasil's trunk in the artwork, as a percentage of the image.
const YGGDRASIL = { x: 51, y: 22 };

const CINEMATIC_REALM_ID = 'midgard';
const ENTER_MS = 1500;
const ENTER_SCALE = 2.6;
const SELECT_SCALE = 1.04;

const SHARED_NAMES = new Set(
  REALMS.map((r) => r.name).filter((name, i, all) => all.indexOf(name) !== i)
);

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

function useElementSize(ref) {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

export default function WorldMapPage() {
  const navigate = useNavigate();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const finePointer = useMediaQuery('(hover: hover) and (pointer: fine)');

  const rootRef = useRef(null);
  const parallaxRef = useRef(null);
  const atmosRef = useRef(null);
  const zoneRefs = useRef([]);
  const listRefs = useRef([]);
  const enterButtonRef = useRef(null);
  const enterTimerRef = useRef(null);
  const enterTargetRef = useRef(null);
  const recoverTimerRef = useRef(null);

  const viewport = useElementSize(rootRef);
  const layout = useMemo(
    () => computeGatewayLayout(viewport.width, viewport.height, BOUNDS, ART_RATIO),
    [viewport.width, viewport.height]
  );

  const [hoveredId, setHoveredId] = useState(null);
  const [selectedRealm, setSelectedRealm] = useState(null);
  const [focusRealm, setFocusRealm] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [phase, setPhase] = useState('idle');

  const isPortrait = layout?.mode === 'portrait';
  const isCompact = Boolean(layout?.compact);
  const motionOn = !reducedMotion;
  const parallaxOn = motionOn && finePointer && phase === 'idle';

  const goToRealm = useCallback(
    (realmId, cinematic) => {
      navigate(`/world/${realmId}`, cinematic ? { state: { fromGateway: true } } : undefined);
    },
    [navigate]
  );

  const finishEnter = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    const target = enterTargetRef.current;
    if (!target) return;
    enterTargetRef.current = null;
    goToRealm(target, true);
    // If the gateway is somehow still mounted afterwards (e.g. an instant Back), make it usable again.
    recoverTimerRef.current = setTimeout(() => {
      setPhase('idle');
      setSelectedRealm(null);
    }, 400);
  }, [goToRealm]);

  useEffect(
    () => () => {
      clearTimeout(enterTimerRef.current);
      clearTimeout(recoverTimerRef.current);
    },
    []
  );

  const selectRealm = useCallback((realm) => {
    setSelectedRealm(realm);
    setFocusRealm(realm);
  }, []);

  const closePanel = useCallback(() => {
    if (!selectedRealm) return;
    const items = isPortrait ? listRefs.current : zoneRefs.current;
    items[REALMS.findIndex((r) => r.id === selectedRealm.id)]?.focus({ preventScroll: true });
    setSelectedRealm(null);
  }, [selectedRealm, isPortrait]);

  const handleEnterRealm = useCallback(() => {
    if (!selectedRealm || phase !== 'idle') return;
    const cinematic = selectedRealm.id === CINEMATIC_REALM_ID && motionOn && !document.hidden;
    if (!cinematic) {
      goToRealm(selectedRealm.id, false);
      return;
    }
    enterTargetRef.current = selectedRealm.id;
    setPhase('entering');
    enterTimerRef.current = setTimeout(finishEnter, ENTER_MS + 60);
  }, [selectedRealm, phase, motionOn, goToRealm, finishEnter]);

  const handleClose = useCallback(() => {
    if (window.history.length <= 2) {
      navigate('/home');
    } else {
      navigate(-1);
    }
  }, [navigate]);

  // Keyboard: arrows move between realms, Esc closes; any key skips the transition.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (phase === 'entering') {
        e.preventDefault();
        finishEnter();
        return;
      }
      if (e.key === 'Escape') {
        if (selectedRealm) closePanel();
        else handleClose();
        return;
      }
      const step =
        e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const items = isPortrait ? listRefs.current : zoneRefs.current;
      const current = items.indexOf(document.activeElement);
      const next = current < 0 ? (step > 0 ? 0 : REALMS.length - 1) : (current + step + REALMS.length) % REALMS.length;
      if (selectedRealm) setSelectedRealm(null);
      items[next]?.focus();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, selectedRealm, isPortrait, finishEnter, closePanel, handleClose]);

  // A transition must never strand the player: finish it if the tab is hidden.
  useEffect(() => {
    if (phase !== 'entering') return undefined;
    const onVisibility = () => {
      if (document.hidden) finishEnter();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [phase, finishEnter]);

  useEffect(() => {
    if (selectedRealm) enterButtonRef.current?.focus({ preventScroll: true });
  }, [selectedRealm]);

  // Pointer parallax, written straight to the two layers so React never re-renders for it.
  useEffect(() => {
    const root = rootRef.current;
    const art = parallaxRef.current;
    const atmos = atmosRef.current;
    if (!root || !art || !atmos) return undefined;
    if (!parallaxOn || !layout) {
      art.style.transform = '';
      atmos.style.transform = '';
      return undefined;
    }

    const reach = Math.min(layout.width * 0.006, 14);
    const current = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let frame = 0;

    const apply = () => {
      art.style.transform = `translate3d(${current.x.toFixed(2)}px, ${current.y.toFixed(2)}px, 0) scale(1.03)`;
      atmos.style.transform = `translate3d(${(current.x * 0.8).toFixed(2)}px, ${(current.y * 0.8).toFixed(2)}px, 0)`;
    };
    const tick = () => {
      current.x += (target.x - current.x) * 0.07;
      current.y += (target.y - current.y) * 0.07;
      apply();
      const settled = Math.abs(target.x - current.x) < 0.05 && Math.abs(target.y - current.y) < 0.05;
      frame = settled ? 0 : requestAnimationFrame(tick);
    };
    const wake = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };
    const onMove = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      target.x = -(e.clientX / root.clientWidth - 0.5) * 2 * reach;
      target.y = -(e.clientY / root.clientHeight - 0.5) * 2 * reach;
      wake();
    };
    const onLeave = () => {
      target.x = 0;
      target.y = 0;
      wake();
    };

    apply();
    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
    };
  }, [parallaxOn, layout]);

  const focusPoint = focusRealm
    ? { x: focusRealm.position.x / 100, y: focusRealm.position.y / 100 }
    : { x: 0.5, y: 0.5 };

  let cameraScale = 1;
  if (phase === 'entering') cameraScale = ENTER_SCALE;
  else if (selectedRealm && motionOn) cameraScale = SELECT_SCALE;

  const cameraStyle = layout
    ? {
        transform: `translate3d(${(focusPoint.x * layout.width * (1 - cameraScale)).toFixed(2)}px, ${(
          focusPoint.y *
          layout.height *
          (1 - cameraScale)
        ).toFixed(2)}px, 0) scale(${cameraScale})`,
      }
    : undefined;

  const dimStyle =
    layout && focusRealm
      ? {
          background: `radial-gradient(ellipse ${Math.round(
            (focusRealm.size.width / 100) * layout.width * 1.05
          )}px ${Math.round((focusRealm.size.height / 100) * layout.height * 1.25)}px at ${Math.round(
            layout.left + focusPoint.x * layout.width
          )}px ${Math.round(layout.top + focusPoint.y * layout.height)}px, transparent 0%, transparent 62%, rgba(3, 5, 10, 0.66) 170%)`,
        }
      : undefined;

  // Dock the details panel away from the selected realm on wide screens.
  const wide = !isPortrait && viewport.width >= 900;
  const panelSide = wide && selectedRealm ? (selectedRealm.position.x < 50 ? 'right' : 'left') : 'center';

  return (
    <div
      ref={rootRef}
      className="gw-root fixed inset-0 z-50 overflow-hidden"
      style={{ '--gw-enter-ms': `${ENTER_MS}ms` }}
      data-testid="world-map-page"
      data-phase={phase}
      data-motion={motionOn ? 'on' : 'off'}
      data-layout={layout?.mode}
      role="main"
      aria-label="Nine Realms Gateway"
      onPointerDownCapture={phase === 'entering' ? finishEnter : undefined}
    >
      <div className="gw-backdrop" style={{ backgroundImage: `url(${WORLD_MAP_IMAGE_MOBILE})` }} aria-hidden="true" />

      {/* ── Stage: artwork, atmosphere and hotspots share one box ── */}
      {layout && (
        <div
          className="gw-stage"
          data-covers={layout.covers ? 'true' : 'false'}
          style={{
            left: layout.left,
            top: layout.top,
            width: layout.width,
            height: layout.height,
            '--gw-u': layout.width / WORLD_MAP_DIMENSIONS.width,
          }}
        >
          <div className="gw-camera" style={cameraStyle}>
            <div className="gw-parallax" ref={parallaxRef}>
              <picture>
                <source media="(max-width: 639px)" srcSet={WORLD_MAP_IMAGE_MOBILE} type="image/webp" />
                <source srcSet={WORLD_MAP_IMAGE} type="image/webp" />
                <img
                  src={WORLD_MAP_IMAGE_PNG}
                  alt="Yggdrasil — The Nine Realms of Norse mythology"
                  className="gw-art"
                  data-loaded={imageLoaded ? 'true' : 'false'}
                  onLoad={() => setImageLoaded(true)}
                  draggable={false}
                />
              </picture>

              {imageLoaded && (
                <>
                  <div className="gw-atmos" ref={atmosRef} aria-hidden="true">
                    <div className="gw-sun" />
                    <div className="gw-tree" style={{ left: `${YGGDRASIL.x}%`, top: `${YGGDRASIL.y}%` }} />
                    {!isCompact && <div className="gw-mist" />}
                  </div>

                  <div aria-hidden="true">
                    {REALMS.map((realm) => {
                      const rgb = REALM_GLOW[realm.id] || DEFAULT_GLOW;
                      const selected = selectedRealm?.id === realm.id;
                      return (
                        <div
                          key={realm.id}
                          className="gw-glow"
                          data-on={selected || hoveredId === realm.id ? 'true' : 'false'}
                          data-selected={selected ? 'true' : 'false'}
                          style={{
                            left: `${realm.position.x}%`,
                            top: `${realm.position.y}%`,
                            width: `${realm.size.width * 1.5}%`,
                            height: `${realm.size.height * 1.7}%`,
                            '--gw-glow-strong': `rgba(${rgb}, 0.5)`,
                            '--gw-glow-soft': `rgba(${rgb}, 0.16)`,
                          }}
                        />
                      );
                    })}
                  </div>

                  <div className="gw-chrome" aria-label="Realm selection map">
                    {REALMS.map((realm, index) => {
                      const rgb = REALM_GLOW[realm.id] || DEFAULT_GLOW;
                      const active = hoveredId === realm.id || selectedRealm?.id === realm.id;
                      return (
                        <button
                          key={realm.id}
                          type="button"
                          ref={(el) => { zoneRefs.current[index] = el; }}
                          tabIndex={isPortrait ? -1 : 0}
                          aria-hidden={isPortrait ? 'true' : undefined}
                          onClick={() => selectRealm(realm)}
                          onMouseEnter={() => setHoveredId(realm.id)}
                          onMouseLeave={() => setHoveredId((id) => (id === realm.id ? null : id))}
                          onFocus={() => setHoveredId(realm.id)}
                          onBlur={() => setHoveredId((id) => (id === realm.id ? null : id))}
                          className="gw-zone"
                          data-active={active ? 'true' : 'false'}
                          style={{
                            left: `${realm.position.x}%`,
                            top: `${realm.position.y}%`,
                            width: `${realm.size.width}%`,
                            height: `${realm.size.height}%`,
                            '--gw-color': `rgb(${rgb})`,
                            '--gw-glow-strong': `rgba(${rgb}, 0.5)`,
                          }}
                          aria-label={`${realm.name} — ${realm.subtitle}`}
                          data-testid={`realm-hotspot-${realm.id}`}
                        >
                          <span className="gw-marker" />
                          {!isCompact && (
                            <span className="gw-label">
                              <strong>{realm.name}</strong>
                              {SHARED_NAMES.has(realm.name) && <small>{realm.subtitle}</small>}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!imageLoaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-20">
          <div className="w-14 h-14 border-4 border-amber-900/30 border-t-amber-500/80 rounded-full animate-spin" />
          <span className="text-amber-400/70 text-xs uppercase tracking-widest">The realms stir…</span>
        </div>
      )}

      {/* ── Edge vignette and selection focus ── */}
      {!isPortrait && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 58%, rgba(0,0,0,0.6) 100%)' }}
          aria-hidden="true"
        />
      )}
      <div className="gw-dim" data-on={selectedRealm ? 'true' : 'false'} style={dimStyle} aria-hidden="true" />

      {/* ── Header ── */}
      <header className="gw-chrome absolute top-0 left-0 right-0 z-30 flex items-center p-3 sm:p-4">
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleClose}
            className="p-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40 hover:border-amber-500/60"
            aria-label="Close Nine Realms Gateway"
            data-testid="close-world-map"
          >
            <X className="w-5 h-5 text-amber-100" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40">
            <Compass className="w-4 h-4 text-amber-500" />
            <span className="font-heading text-amber-100 text-xs sm:text-sm uppercase tracking-widest">
              The Nine Realms
            </span>
          </div>
        </div>
      </header>

      {/* ── Portrait: realm list under the map ── */}
      {isPortrait && layout && (
        <div
          className="gw-chrome absolute inset-x-0 bottom-0 z-20 overflow-y-auto overscroll-contain"
          style={{ top: layout.top + layout.height }}
          data-testid="mobile-realm-list"
        >
          <div className="p-3 pb-10 grid gap-2 sm:grid-cols-2">
            <p className="text-amber-400/70 text-xs uppercase tracking-widest sm:col-span-2 px-1">
              Choose your destination
            </p>
            {REALMS.map((realm, index) => {
              const rgb = REALM_GLOW[realm.id] || DEFAULT_GLOW;
              const active = hoveredId === realm.id || selectedRealm?.id === realm.id;
              return (
                <button
                  key={realm.id}
                  type="button"
                  ref={(el) => { listRefs.current[index] = el; }}
                  onClick={() => selectRealm(realm)}
                  onFocus={() => setHoveredId(realm.id)}
                  onBlur={() => setHoveredId((id) => (id === realm.id ? null : id))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                    active ? 'bg-black/80 border-amber-400/70' : 'bg-black/55 border-amber-900/40'
                  }`}
                  data-testid={`mobile-realm-${realm.id}`}
                >
                  <span
                    className="w-8 h-8 rounded-full shrink-0 border"
                    style={{
                      background: `radial-gradient(circle, rgba(${rgb}, 0.95) 0%, rgba(${rgb}, 0.25) 55%, transparent 72%)`,
                      borderColor: `rgba(${rgb}, 0.55)`,
                    }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0">
                    <span className="block font-heading font-bold text-sm text-amber-100">{realm.name}</span>
                    <span className="block text-xs text-amber-300/60 truncate">{realm.subtitle}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-amber-500/60 shrink-0 ml-auto" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Selected realm panel ── */}
      {selectedRealm && (
        <div
          className={`gw-chrome absolute bottom-0 z-30 p-3 sm:p-5 ${motionOn ? 'animate-slideUp' : ''} ${
            panelSide === 'left' ? 'left-0 w-full max-w-md' : panelSide === 'right' ? 'right-0 w-full max-w-md' : 'left-0 right-0'
          }`}
          data-testid="realm-info-panel"
          role="dialog"
          aria-label={`${selectedRealm.name} details`}
        >
          <div
            className={`max-w-xl mx-auto rounded-2xl overflow-hidden backdrop-blur-xl bg-gradient-to-br border border-amber-900/40 ${selectedRealm.theme.bgGradient}`}
          >
            <div className="p-4 sm:p-5 border-b border-white/10 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-0.5">
                  {selectedRealm.subtitle}
                </p>
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-white leading-tight">
                  {selectedRealm.name}
                </h2>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{selectedRealm.description}</p>
              </div>
              <button
                onClick={closePanel}
                className="p-1.5 rounded-full hover:bg-white/10 shrink-0"
                aria-label="Close realm details"
                data-testid="close-realm-panel"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-white/45 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span>Realm accessible</span>
              </div>
              <button
                ref={enterButtonRef}
                onClick={handleEnterRealm}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold ${
                  motionOn ? 'transition-transform duration-200 hover:scale-105 active:scale-95' : ''
                } focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300`}
                style={{
                  backgroundColor: selectedRealm.theme.primary,
                  color: ['niflheim', 'alfheim', 'asgard'].includes(selectedRealm.id) ? '#000' : '#fff',
                }}
                data-testid="enter-realm-btn"
                aria-label={`Enter ${selectedRealm.name}`}
              >
                Enter {selectedRealm.name}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hints ── */}
      {!selectedRealm && imageLoaded && !isPortrait && (
        <div className="gw-chrome absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none text-center">
          <p className="text-amber-100/60 text-[11px] uppercase tracking-widest">Select a realm to begin your journey</p>
          {finePointer && (
            <p className="mt-1 text-amber-100/35 text-[10px] uppercase tracking-wide">
              Arrow keys to navigate · Enter to select · Esc to close
            </p>
          )}
        </div>
      )}

      {/* ── Midgard transition veil (any tap or key skips it) ── */}
      <div className="gw-veil z-40" aria-hidden="true" />
    </div>
  );
}
